import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronUp, MessageCircle, Crown, Heart, Gem, GraduationCap, Headphones, Bus, Brain, Download, FileText, Map as MapIcon } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { ShareButtons } from './ShareButtons';
import { COURT_RUSH_BRIEF_MD, downloadText } from './treasury/claudeDesignDownloads';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
export const Footer = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    language
  } = useLanguage();
  const t = translations[language];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <footer className="bg-black text-white relative overflow-hidden" data-id="element-1244" data-mp="29ydxq3q81abh">
      <div className="absolute inset-0 bg-gradient-to-t from-amber-900/10 to-transparent opacity-20" data-id="element-1245" data-mp="2eyt9wryrobrv"></div>
      <div className="container mx-auto px-4 py-20 relative z-10" data-id="element-1246" data-mp="3l8qx26bccnsi">
        {/* Founding Members CTA */}
        <div className="max-w-4xl mx-auto mb-16 border border-amber-400/50 bg-amber-400/[0.06] p-8 sm:p-10" data-id="element-1247" data-mp="3gbinuv867u3e">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8" data-id="element-1248" data-mp="3v8hrk7zwj2sl">
            <div data-id="element-1249" data-mp="3q7u1bojhypzf">
              <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-medium mb-3 flex items-center gap-2" data-id="element-1250" data-mp="3q7bw42eux1fd">
                <Crown className="w-3.5 h-3.5" data-id="element-1251" data-mp="80ntcj7a4qcv" />
                Founding Members
              </p>
              <h4 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-1252" data-mp="3230j4ucx8yg">
                Put your name on the court before the doors open.
              </h4>
              <p className="text-white/60 text-sm font-light leading-relaxed max-w-xl" data-id="element-1253" data-mp="cuiiw39kfzjv">
                We&rsquo;re assembling a small founding cohort behind Canada&rsquo;s
                most ambitious immersive experience. Book a 25-minute call with
                Ash Singh — no big cheque required.
              </p>
            </div>
            <a href="/founding-members" className="shrink-0 inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 text-[11px] tracking-[0.22em] uppercase font-bold transition-colors duration-150" data-id="element-1254" data-mp="ytxyzp4ywl2j">
              Become a Founding Member
              <ArrowRight className="w-4 h-4" data-id="element-1255" data-mp="1ckknypkxewlf" />
            </a>
          </div>
        </div>
        {/* Language Selector */}
        <div className="flex justify-center mb-12" data-id="element-1256" data-mp="1hj4bguf64gfw">
          <LanguageSelector data-id="element-1257" data-mp="1mj8twpzmzl8r" />
        </div>
        {/* Share Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mx-auto mb-20 border border-amber-400/30 p-8 bg-black/40" data-id="element-1258" data-mp="1mhxrathxgjj8">
          <ShareButtons layout="horizontal" title="Share This Cultural Experience" data-id="element-1259" data-mp="2agygfc9oj9re" />
        </motion.div>
        {/* FAQ Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-4xl mx-auto mb-20" data-id="element-1260" data-mp="21nbjvjxne2h5">
          <h4 className="text-center font-serif text-3xl text-amber-400 mb-12 tracking-wider uppercase" data-id="element-1261" data-mp="2kcrytca2xxh3">
            {t.faqTitle}
          </h4>
          <div className="space-y-4" data-id="element-1262" data-mp="2fdow3yhko3ca">
            {t.faqQuestions.map((faq, index) => <div key={index} className="border border-amber-400/30 bg-black/40 overflow-hidden" data-id="element-1263" data-mp="18zpj0jft1bxz" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="flex justify-between items-center w-full p-6 text-left" data-id="element-1264" data-mp="2rvr518btgcc6">
                  <span className="text-white font-light pr-4" data-id="element-1265" data-mp="2wok9rw7abko9">
                    {faq.question}
                  </span>
                  {openFaq === index ? <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1266" data-mp="2nuxl0q1hvhok" /> : <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1267" data-mp="3bk8dzlcls7s5" />}
                </button>
                {openFaq === index && <div className="px-6 pb-6 text-white/70 font-light leading-relaxed border-t border-amber-400/20 pt-4" data-id="element-1268" data-mp="31mc40wvejivm">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </motion.div>
        {/* Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 border-t border-amber-400/30 pt-16" data-id="element-1269" data-mp="1v604arcxlsxx">
          {/* Brand */}
          <div className="md:col-span-2" data-id="element-1270" data-mp="1nkmtqnxwv9ca">
            <div className="flex items-center mb-6" data-id="element-1271" data-mp="1imv0nqlj5h6i">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a1thpmaUHZUisJ3HgEhAjA/oldkhanda.png" alt="Khanda Symbol" className="w-10 h-10 mr-3 object-contain" data-id="element-1272" data-mp="1im33ibcabbe4" />
              <h3 className="font-serif text-2xl text-amber-400" data-id="element-1273" data-mp="1rlq7n4weevav">
                {t.footerTitle}
              </h3>
            </div>
            <p className="text-white/60 mb-6 max-w-md font-light leading-relaxed" data-id="element-1274" data-mp="zwwq93u9fbzp">
              {t.footerDescription}
            </p>
          </div>
          {/* Quick Links */}
          <div data-id="element-1275" data-mp="18xbg8gi65f9j">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-1276" data-mp="18wjmlb84trlw">
              {t.quickLinks}
            </h4>
            <ul className="space-y-3" data-id="element-1277" data-mp="l5nvyhfex2g8">
              {t.links.map((link, index) => <li key={index} data-id="element-1278" data-mp="g7ma5oqlqp12" data-mpi={index}>
                  <a href={link.href} className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-1279" data-mp="g6jmdpokt2pd">
                    {link.text}
                  </a>
                </li>)}
              <li data-id="element-1280" data-mp="2q0yoqo5odoo1">
                <a href="/blueprint" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-1281" data-mp="2q2jgy74ioviq">
                  Experience Blueprint
                </a>
              </li>
              <li data-id="element-1282" data-mp="22bxb8mhzjnbw">
                <a href="/founding-members" className="text-amber-400/85 hover:text-amber-300 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1283" data-mp="22be480tvijcn">
                  <Crown className="w-3 h-3" data-id="element-1284" data-mp="2bcvjjgzgih0a" />
                  Become a Founding Member
                </a>
              </li>
              <li data-id="element-1285" data-mp="3mqpjmohvpdud">
                <a href="/kingdom-circle" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1286" data-mp="3mq6b1ye4c1t0">
                  <Crown className="w-3 h-3" data-id="element-1287" data-mp="1718yqy9vp3aj" />
                  Kingdom Circle
                </a>
              </li>
              <li data-id="element-1288" data-mp="12a0ndwozn7uq">
                <a href="/sikh-heritage-forward" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1289" data-mp="129hboxpxlfp5">
                  <Heart className="w-3 h-3" data-id="element-1290" data-mp="sarul75uzz9y" />
                  Sikh Heritage Forward
                </a>
              </li>
              <li data-id="element-1291" data-mp="jc6inuuw7ii5">
                <a href="/treasure" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1292" data-mp="jbobi36ozjvg">
                  <Gem className="w-3 h-3" data-id="element-1293" data-mp="2dqqvxmgvwbri" />
                  Treasures of the Sikh Empire
                </a>
              </li>
              <li data-id="element-1294" data-mp="28sfm3bqla19a">
                <a href="/court-rush" className="text-amber-400/85 hover:text-amber-300 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1295" data-mp="28rw7a3w8jszh">
                  <Crown className="w-3 h-3" data-id="element-1296" data-mp="1mmxy4m38wdl5" />
                  Court Rush
                </a>
              </li>
              <li data-id="element-1297" data-mp="17u4h2w8brped">
                <a href="/restore-punjab" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1298" data-mp="1cus6jdgwny6h">
                  <MapIcon className="w-3 h-3" data-id="element-1299" data-mp="12xdtzoy3rtjg" />
                  Restore Punjab
                </a>
              </li>
              <li data-id="element-1300" data-mp="oy4nxi3m2ft7">
                <a href="/schools" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1301" data-mp="oynwi87dfruk">
                  <GraduationCap className="w-3 h-3" data-id="element-1302" data-mp="2jbngedisb87n" />
                  For Schools &amp; Curriculum Partners
                </a>
              </li>
              <li data-id="element-1303" data-mp="cmls4it77z6m">
                <a href="/sponsor-a-bus" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1304" data-mp="cm2cj8r0tmvr">
                  <Bus className="w-3 h-3" data-id="element-1305" data-mp="mdq89lum1q31" />
                  Sponsor a Bus
                </a>
              </li>
              <li data-id="element-1306" data-mp="hl6jrtudxajg">
                <a href="/house-of-lions" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1307" data-mp="hkncr869w6k7">
                  <Headphones className="w-3 h-3" data-id="element-1308" data-mp="qgct32sv6aqq" />
                  House of Lions Podcast
                </a>
              </li>
              <li data-id="element-1309" data-mp="10cpktv4umeiu">
                <a href="/ai-entrepreneurship" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1310" data-mp="10c6fddwdxilp">
                  <Brain className="w-3 h-3" data-id="element-1311" data-mp="324ujdpgusa2j" />
                  Entrepreneurship and AI Opportunity
                </a>
              </li>
              <li data-id="element-1312" data-mp="is5nbkjaelh1">
                <a href="/sponsor-deck" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1313" data-mp="dv5h99mftipx">
                  <Crown className="w-3 h-3" data-id="element-1314" data-mp="3wzi2zs0mqor" />
                  Sponsor Deck
                </a>
              </li>
            </ul>
          </div>
          {/* Contact */}
          <div data-id="element-1315" data-mp="11msyt0xt7hfj">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-1316" data-mp="wln5b0wbrq4y">
              {t.contactUs}
            </h4>
            <a href="https://chat.whatsapp.com/Joakmqmpdd3A0riGDdBEEb?mode=wwc" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-medium py-3 px-6 transition-colors text-sm" data-id="element-1317" data-mp="rtvc0ilsb6b4">
              <MessageCircle className="w-4 h-4" data-id="element-1318" data-mp="1fiy12bir01fn" />
              {t.subscribe}
            </a>
          </div>
        </div>

        {/* Claude Design resource handoff */}
        <motion.details initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-4xl mx-auto mb-16 border border-amber-400/25 bg-black/40 group" data-id="element-1319" data-mp="1rvs2lg661zvz">
          <summary className="cursor-pointer list-none px-5 sm:px-7 py-5 flex items-center justify-between gap-5" data-id="element-1320" data-mp="2asjjxk25e4xe">
            <div className="flex items-center gap-3 text-left" data-id="element-1321" data-mp="2fkusfbayez1k">
              <div className="w-9 h-9 shrink-0 border border-amber-400/40 bg-amber-400/10 flex items-center justify-center" data-id="element-1322" data-mp="2kjnwkd4cpdp2">
                <FileText className="w-4 h-4 text-amber-400" data-id="element-1323" data-mp="2pjsca0xfq4e8" />
              </div>
              <div data-id="element-1324" data-mp="2uin7y3gi3nfr">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-medium mb-1" data-id="element-1325" data-mp="2ui3o2h7aioz3">
                  Claude Design
                </p>
                <p className="text-white/60 text-xs sm:text-sm font-light" data-id="element-1326" data-mp="1zulkivmjnvh5">
                  Court Rush product brief and design resource
                </p>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-amber-400/70 shrink-0 transition-transform duration-300 group-open:rotate-180" data-id="element-1327" data-mp="29st0pc90e9i3" />
          </summary>

          <div className="border-t border-amber-400/15 px-5 sm:px-7 py-6" data-id="element-1328" data-mp="2nid0c97amqn6">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-end" data-id="element-1329" data-mp="2ijtcg39534s6">
              <div data-id="element-1330" data-mp="2xidms40glmvl">
                <p className="font-serif text-xl sm:text-2xl text-white mb-2" data-id="element-1331" data-mp="2xgssmfi245y9">
                  Court Rush by Lahore Treasury
                </p>
                <p className="text-white/60 text-sm font-light leading-relaxed max-w-2xl" data-id="element-1332" data-mp="1342wrs84omo8">
                  A fast, viral 15-minute Misl challenge: rally five people,
                  open Court Packets, awaken three memories, and compete for
                  free early access to the Lahore Court Preview at Illuminarium
                  Toronto.
                </p>
                <p className="mt-3 text-amber-400/75 text-[10px] tracking-[0.22em] uppercase" data-id="element-1333" data-mp="3eqmv5gngwgyu">
                  Start a Misl. Rally your people. Open the Court Gates.
                </p>
              </div>

              <button type="button" onClick={event => {
              event.preventDefault();
              downloadText('court-rush-brief.md', COURT_RUSH_BRIEF_MD, 'text/markdown;charset=utf-8');
            }} className="inline-flex items-center justify-center gap-2 border border-amber-400/45 hover:border-amber-400 hover:bg-amber-400/10 text-amber-400 px-5 py-3 text-[10px] tracking-[0.22em] uppercase font-medium transition-colors" data-id="element-1334" data-mp="34zjweli0ge2q">
                <Download className="w-3.5 h-3.5" data-id="element-1335" data-mp="kamcdh32ylz9" />
                Download Brief
              </button>
            </div>

            <div className="mt-6 pt-5 border-t border-amber-400/10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] font-light" data-id="element-1336" data-mp="2bffv4pbmsxsn">
              <p className="text-white/50" data-id="element-1337" data-mp="27d2pe0wvwzvv">
                <span className="block text-amber-400/80 text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1338" data-mp="27fyohskgxulw">
                  Core loop
                </span>
                5 people · 10 card reveals · 3 memories
              </p>
              <p className="text-white/50" data-id="element-1339" data-mp="1soy2ekqsu27a">
                <span className="block text-amber-400/80 text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1340" data-mp="1so6abjweumlr">
                  Reward
                </span>
                Up to 10 free preview seats per winning Misl
              </p>
              <p className="text-white/50" data-id="element-1341" data-mp="1jm6rqn5qzagp">
                <span className="block text-amber-400/80 text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1342" data-mp="1epq5xzm0t86s">
                  Scope
                </span>
                19 screens · viral V1 · no discounts or campaign mechanics
              </p>
            </div>
          </div>
        </motion.details>

        {/* Logo & Copyright */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center border-t border-amber-400/30 pt-12" data-id="element-1343" data-mp="3qayyzjs9nmo6">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center mb-8" data-id="element-1344" data-mp="37m25hi4p0y70">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aqXvi2nxLJFgni6jP9pU4X/image.png" alt="Sikh Kingdoms Logo" className="w-32 h-32 object-cover rounded-full opacity-80" data-id="element-1345" data-mp="2ny1psyvd1vi6" />
          </motion.div>
          <p className="text-white/40 text-xs tracking-wider uppercase font-light" data-id="element-1346" data-mp="3m9m6ts7z3o1b">
            {t.copyright}
            <br data-id="element-1347" data-mp="4vgyx94ybaqr" />
            {t.allRightsReserved}
          </p>
        </motion.div>
      </div>
    </footer>;
};